import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useReceivingOrder,
  useStartReceiving,
  useAddReceivingLine,
  useReceiveLine,
  useUpdateLineQcStatus,
  useRemoveReceivingLine,
  useCompleteReceiving,
  useCancelReceiving,
} from '../hooks/useWmsReceiving';
import { ReceivingStatusBadge } from '../components/receiving/ReceivingStatusBadge';
import { ReceivingLinesTable } from '../components/receiving/ReceivingLinesTable';
import { ReceivingDocumentsTab } from '../components/receiving/ReceivingDocumentsTab';
import { AddReceivingLineModal } from '../components/receiving/AddReceivingLineModal';
import { ReceiveLineModal } from '../components/receiving/ReceiveLineModal';
import { UpdateQCModal } from '../components/receiving/UpdateQCModal';
import { ConfirmDialog, PromptDialog } from '../components/shared/ConfirmDialog';
import type {
  ReceivingOrder,
  ReceivingLine,
  AddReceivingLineInput,
  ReceiveLineInput,
  UpdateQCStatusInput,
} from '../types';

type DetailTab = 'details' | 'documents';

export default function ReceivingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<DetailTab>('details');

  // Modal state
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [receiveLine, setReceiveLine] = useState<ReceivingLine | null>(null);
  const [qcLine, setQcLine] = useState<ReceivingLine | null>(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [lineToRemove, setLineToRemove] = useState<ReceivingLine | null>(null);

  // Queries
  const receivingOrderQuery = useReceivingOrder(id || '');

  // Mutations
  const startMutation = useStartReceiving();
  const addLineMutation = useAddReceivingLine();
  const receiveLineMutation = useReceiveLine();
  const updateQCMutation = useUpdateLineQcStatus();
  const removeLineMutation = useRemoveReceivingLine();
  const completeMutation = useCompleteReceiving();
  const cancelMutation = useCancelReceiving();

  const receivingOrder = receivingOrderQuery.data as ReceivingOrder | undefined;
  const lines = receivingOrder?.lines || [];

  const handleStart = async () => {
    if (!id) return;
    await startMutation.mutateAsync(id);
  };

  const handleAddLine = async (data: AddReceivingLineInput) => {
    if (!id) return;
    await addLineMutation.mutateAsync({ orderId: id, data });
  };

  const handleReceiveLine = async (data: ReceiveLineInput) => {
    if (!id || !receiveLine) return;
    await receiveLineMutation.mutateAsync({
      orderId: id,
      lineId: receiveLine.id,
      data,
    });
  };

  const handleUpdateQC = async (data: UpdateQCStatusInput) => {
    if (!id || !qcLine) return;
    await updateQCMutation.mutateAsync({
      orderId: id,
      lineId: qcLine.id,
      data,
    });
  };

  const handleRemoveLine = async () => {
    if (!id || !lineToRemove) return;
    await removeLineMutation.mutateAsync({ orderId: id, lineId: lineToRemove.id });
    setLineToRemove(null);
  };

  const handleComplete = async () => {
    if (!id) return;
    await completeMutation.mutateAsync(id);
  };

  const handleCancel = async (reason?: string) => {
    if (!id) return;
    await cancelMutation.mutateAsync({ id, reason: reason || undefined });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  if (receivingOrderQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (receivingOrderQuery.error || !receivingOrder) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Receiving Order not found</h2>
        <p className="text-muted-foreground mt-2">
          The receiving order you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/procurement">Back to Receiving</Link>
        </Button>
      </div>
    );
  }

  const canStart = receivingOrder.status === 'draft' && lines.length > 0;
  const canAddLines = receivingOrder.status === 'draft';
  const allLinesProcessed = lines.length > 0 && lines.every(
    (l) => l.line_status === 'complete' || l.line_status === 'partial' || l.line_status === 'rejected'
  );
  const canComplete = (receivingOrder.status === 'in_progress' || receivingOrder.status === 'pending_qc') && allLinesProcessed;
  const canCancel = !['completed', 'cancelled'].includes(receivingOrder.status);

  const totalLines = receivingOrder.total_lines ?? receivingOrder.line_count ?? lines.length;
  const receivedLines = receivingOrder.received_lines ?? receivingOrder.lines_complete ?? 0;

  const getProgress = () => {
    if (!totalLines || totalLines === 0) return 0;
    return Math.round((receivedLines / totalLines) * 100);
  };

  const TABS: { value: DetailTab; label: string }[] = [
    { value: 'details', label: 'Details' },
    { value: 'documents', label: 'Documents' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/procurement"
              className="text-muted-foreground hover:text-foreground"
            >
              Receiving
            </Link>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-2xl font-bold tracking-tight font-mono">
              {receivingOrder.order_number}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            {receivingOrder.po_number && `PO: ${receivingOrder.po_number}`}
            {receivingOrder.supplier_name && ` • ${receivingOrder.supplier_name}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {canAddLines && (
            <Button variant="outline" onClick={() => setShowAddLineModal(true)}>
              Add Line
            </Button>
          )}
          {canStart && (
            <Button onClick={handleStart} disabled={startMutation.isPending}>
              {startMutation.isPending ? 'Starting...' : 'Start Receiving'}
            </Button>
          )}
          {(receivingOrder.status === 'in_progress' || receivingOrder.status === 'pending_qc') && (
            <Button
              onClick={() => setShowCompleteConfirm(true)}
              disabled={!canComplete || completeMutation.isPending}
              title={!allLinesProcessed ? 'All lines must be received before completing' : undefined}
            >
              {completeMutation.isPending ? 'Completing...' : 'Complete'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              className="text-red-600"
              onClick={() => setShowCancelPrompt(true)}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.value
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'documents' && (
        <ReceivingDocumentsTab order={receivingOrder} />
      )}

      {activeTab === 'details' && <>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReceivingStatusBadge status={receivingOrder.status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Expected Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{formatDate(receivingOrder.expected_date)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${getProgress()}%` }}
                />
              </div>
              <span className="text-sm font-medium">
                {receivedLines}/{totalLines}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Lines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalLines}</p>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">PO Number:</span>
              <span className="font-medium">{receivingOrder.po_number || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Supplier:</span>
              <span>{receivingOrder.supplier_name || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expected:</span>
              <span>{formatDate(receivingOrder.expected_date)}</span>
            </div>
            {receivingOrder.received_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Received:</span>
                <span>{formatDate(receivingOrder.received_date)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span>
                {formatDateTime(receivingOrder.created_at)}
                {receivingOrder.created_by_name && ` by ${receivingOrder.created_by_name}`}
              </span>
            </div>
            {receivingOrder.started_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span>
                  {formatDateTime(receivingOrder.started_at)}
                  {receivingOrder.started_by_name && ` by ${receivingOrder.started_by_name}`}
                </span>
              </div>
            )}
            {receivingOrder.completed_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Completed:</span>
                <span>
                  {formatDateTime(receivingOrder.completed_at)}
                  {receivingOrder.completed_by_name && ` by ${receivingOrder.completed_by_name}`}
                </span>
              </div>
            )}
            {receivingOrder.cancelled_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cancelled:</span>
                <span>{formatDateTime(receivingOrder.cancelled_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {(receivingOrder.notes || receivingOrder.cancellation_reason) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {receivingOrder.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{receivingOrder.notes}</p>
              </CardContent>
            </Card>
          )}
          {receivingOrder.cancellation_reason && (
            <Card>
              <CardHeader>
                <CardTitle>Cancellation Reason</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{receivingOrder.cancellation_reason}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Lines */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Receiving Lines</CardTitle>
          {canAddLines && (
            <Button size="sm" variant="outline" onClick={() => setShowAddLineModal(true)}>
              Add Line
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <ReceivingLinesTable
            lines={lines}
            orderStatus={receivingOrder.status}
            onReceive={setReceiveLine}
            onUpdateQC={setQcLine}
            onRemove={(line) => setLineToRemove(line)}
          />
        </CardContent>
      </Card>

      </> /* end details tab */}

      {/* Modals */}
      <AddReceivingLineModal
        open={showAddLineModal}
        onClose={() => setShowAddLineModal(false)}
        onSubmit={handleAddLine}
        isLoading={addLineMutation.isPending}
      />

      <ReceiveLineModal
        open={!!receiveLine}
        onClose={() => setReceiveLine(null)}
        onSubmit={handleReceiveLine}
        line={receiveLine}
        isLoading={receiveLineMutation.isPending}
      />

      <UpdateQCModal
        open={!!qcLine}
        onClose={() => setQcLine(null)}
        onSubmit={handleUpdateQC}
        line={qcLine}
        isLoading={updateQCMutation.isPending}
      />

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={showCompleteConfirm}
        onOpenChange={setShowCompleteConfirm}
        title="Complete Receiving Order"
        description="Are you sure you want to complete this receiving order? Items will be added to inventory."
        confirmLabel="Complete"
        onConfirm={handleComplete}
        isLoading={completeMutation.isPending}
      />

      <PromptDialog
        open={showCancelPrompt}
        onOpenChange={setShowCancelPrompt}
        title="Cancel Receiving Order"
        description={`Are you sure you want to cancel receiving order "${receivingOrder?.order_number}"?`}
        label="Cancellation reason"
        placeholder="Enter reason (optional)"
        confirmLabel="Cancel Order"
        onConfirm={handleCancel}
        isLoading={cancelMutation.isPending}
      />

      <ConfirmDialog
        open={!!lineToRemove}
        onOpenChange={(open) => !open && setLineToRemove(null)}
        title="Remove Line"
        description={`Are you sure you want to remove "${lineToRemove?.sku || lineToRemove?.sku_code || lineToRemove?.description || 'this line'}"?`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleRemoveLine}
        isLoading={removeLineMutation.isPending}
      />
    </div>
  );
}
